import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import * as Sentry from "@sentry/react";
import { parseJsonToFields } from "../../../../chat-report-panel/utils";
import AnokyeApiService from "@minohealth/common/services/AnokyeApiService";
import { parseAnokyeError } from "@/services/anokye-error-handler";

function useReviews({ reportId, showEditReportPage }) {
  const { user } = useSelector((state) => state.app.datastate);
  const [parsedReportReviews, setParsedReportReviews] = useState([]);
  const [reportReviews, setReportReviews] = useState([]);

  useEffect(() => {
    async function getReportReviews() {
      try {
        const { data } = await AnokyeApiService(user.userToken).GetReviews({
          reviewType: "allReviews",
          reportId: reportId,
        });
        const { moremiReport: originalReport, reviewVersions: reportReviewHistory } = data
        setParsedReportReviews(transformReports(reportReviewHistory, originalReport));
        setReportReviews([...reportReviewHistory, { timestamp: new Date().toISOString(), files: [] }]);
      } catch (error) {
        const parsedError = parseAnokyeError(error);

        Sentry.captureException(error, {
          extra: {
            context: 'useReviews - getReportReviews',
            reportId: reportId || 'undefined',
            reviewType: 'allReviews',
            userToken: user?.userToken ? 'present' : 'missing',
            userId: user?._id || 'undefined',
            errorCode: parsedError.code,
            errorMessage: parsedError.message
          }
        });
        getReportBeforeFirstReview();
      }
    }

    async function getReportBeforeFirstReview() {
      try {
        const { data: unreviewedReport } = await AnokyeApiService(user?.userToken).GetResources({
          resourceType: "reportById",
          reportId: reportId,
          userId: user?._id,
        });
        setParsedReportReviews([transformReportData(unreviewedReport)]);
        setReportReviews([]);
      } catch (error) {
        const parsedError = parseAnokyeError(error);

        Sentry.captureException(error, {
          extra: {
            context: 'useReviews - getReportBeforeFirstReview',
            reportId: reportId || 'undefined',
            resourceType: 'reportById',
            userToken: user?.userToken ? 'present' : 'missing',
            userId: user?._id || 'undefined',
            errorCode: parsedError.code,
            errorMessage: parsedError.message
          }
        });
        console.error('Error loading report:', parsedError.message);
      }
    }

    if (!showEditReportPage) {
      getReportReviews();
    }
  }, [user?._id, showEditReportPage]);

  return { reportReviews, parsedReportReviews };
}

function transformReports(reports, originalReport) {
  const newReportVersions = reports?.flatMap((report) => {
    return report?.files
      ?.map((file) => {
        if (typeof file.data === "string") {
          try {
            file.data = JSON.parse(file.data);
          } catch (e) {
            Sentry.captureException(e, {
              extra: {
                context: 'useReviews - transformReports JSON.parse',
                filename: file?.filename || 'undefined',
                dataType: typeof file.data,
                dataLength: file.data?.length || 0,
                reportId: file.data?._id || 'undefined'
              }
            });
            console.error("Invalid JSON string in data field", e);
            return null;
          }
        }

        return {
          filename: file.filename,
          reviewerId: file.reviewerId,
          reviewerDetails: file.reviewerDetails,
          patient: file.data?.patientInfo || null,
          facility: file.data?.facilityInfo || null,
          modality: getModalityLabel(file.data?.modality) || null,
          technique: file.data?.technique || null,
          examinationReason: file.data?.examinationReason || null,
          clinicalHistory: file.data?.clinicalHistory || null,
          date: file?.data?.date || null,
          content: parseJsonToFields(file?.data?.current),
          og_content: file?.data?.original,
          id: file.data?._id || null,
        };
      })
      .filter(Boolean);
  });

  return [...newReportVersions, transformReportData(originalReport)]
}

function transformReportData(file) {
  return {
    filename: file.filename || null,
    reviewerId: file.reviewerId || null,
    reviewerDetails: file.reviewerDetails || null,
    patient: file.patientInfo || null,
    facility: file.facilityInfo || null,
    modality: getModalityLabel(file.modality) || null,
    technique: file.technique || null,
    examinationReason: file.examinationReason || null,
    clinicalHistory: file.clinicalHistory || null,
    date: file.date || null,
    content: parseJsonToFields(file.current),
    og_content: file.original,
    id: file._id || null,
  };
}

function getModalityLabel(modality) {
  let item = MODALITIES.find((item) => item.value === modality);
  return item?.label ?? "Select modality";
}

const MODALITIES = [
  { label: "Abdominal Ultrasound", value: "abdominal ultrasound" },
  { label: "Angiography", value: "angiography" },
  { label: "Barium Swallow", value: "barium swallow" },
  { label: "Breast Ultrasound", value: "breast ultrasound" },
  { label: "Bronchoscopy", value: "bronchoscopy" },
  { label: "Chest X-ray", value: "chest x-ray" },
  { label: "Colonoscopy", value: "colonoscopy" },
  { label: "CT", value: "ct" },
  { label: "Dermatology (SOAP)", value: "dermatology" },
  { label: "Echocardiography", value: "echocardiography" },
  { label: "Electrocardiogram", value: "electrocardiogram" },
  { label: "Gastroscopy", value: "gastroscopy" },
  { label: "Hysterosalpingogram", value: "hysterosalpingogram" },
  { label: "Intravenous Urogram", value: "intravenous urogram" },
  { label: "Limb X-ray", value: "limb x-ray" },
  { label: "Malaria Microscopy", value: "malaria microscopy" },
  { label: "Mammogram", value: "mammogram" },
  { label: "MRI", value: "mri" },
  { label: "Myelogram", value: "myelogram" },
  { label: "Ophthalmoscopy", value: "ophthalmoscopy" },
  { label: "Other", value: "others" },
  { label: "Clinical Summary", value: "clinical_summary" },
  { label: "Laboratory Interpretation", value: "laboratory" },
  { label: "Pathology", value: "pathology" },
  { label: "Retrograde Urethrogram", value: "retrograde urethrogram" },
  { label: "Sialogram", value: "sialogram" },
  { label: "Spine X-ray", value: "spine x-ray" },
  { label: "Ultrasound", value: "ultrasound" },
  { label: "Voiding Cystourethrogram", value: "voiding cystourethrogram" },
  { label: "X-ray", value: "x-ray" },
].sort((a, b) => {
  // Arrange in alphabetical order and put "other" value at the bottom
  if (b.value === "others") return -1;
  if (a.value === "others") return 1;
  return a.label.localeCompare(b.label);
});

export default useReviews;
